import {
  Box,
  Flex,
  Icon,
  Input,
  Text,
  Image,
  useDisclosure,
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/react';
import { useRef, useState, useEffect, useMemo } from 'react';
import { CustomUploadIcon } from '../../../theme/icons/CustomUploadIcon';
import { CustomCloseIcon } from '../../../theme/icons/CustomCloseIcon';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  setDesignImage,
  removeDesignImage,
  setAppleEnhancedDesignImage,
} from '../../../store/redux/slices/template.slice';
import {
  setError,
  clearError,
} from '../../../store/redux/slices/templateErrors.slice';
import CropModal from './CropModal';
import { getImageDimensions } from './utils/imageDimensions';
import {
  getRevalidationTargets,
  getSharedFallbackImage,
} from './utils/imageHelpers';
import { GOOGLE_DEFAULT_WAVE_BANNER_BASE64 } from '../../../utils/base64';
import { isStockDesignImage } from './utils/isStockDesignImage';
import { Platform, ImageField } from '../../../store/types/template';
import { APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE } from './utils/appleEnhancedDesignConstants';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';

type EnhancedField = 'artwork' | 'secondaryLogo' | 'venueMap';

interface BaseImageUploadFieldProps {
  label: string;
  size?: 'compact' | 'wide';
  isRequired?: boolean;
  isDisabled?: boolean;
  demoPreview?: string;
  errors: Record<string, string>;
  expressivePassEnabled?: boolean;
  validateSection?: (context?: any) => void;
  validateField: (
    field: string,
    value: any,
    context?: any,
    immediate?: boolean
  ) => void;
}

type ImageUploadFieldProps = BaseImageUploadFieldProps &
  (
    | { platform: Platform; field: ImageField; source?: never }
    | { source: 'enhancedEventTicket'; field: EnhancedField; platform?: never }
  );

const ImageUploadField = (props: ImageUploadFieldProps) => {
  const isOverrideUpload =
    props.source === undefined && props.platform !== 'shared';
  const isStandardUpload = !isOverrideUpload;
  const {
    label,
    size = 'wide',
    isRequired = false,
    isDisabled = false,
    demoPreview,
    errors,
    validateField,
  } = props;

  const isEnhanced = props.source === 'enhancedEventTicket';
  const field = props.field;

  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const templateType = useAppSelector((s) => s.template.templateType);
  const focusedField = useAppSelector((s) => s.template.focusedField);
  const design = useAppSelector((s) => s.template.images);
  const enhancedImages = useAppSelector(
    (s) => s.template.appleEnhancedDesign.images
  );
  const enhancedDesignType = useAppSelector(
    (s) => s.template.appleEnhancedDesign.type
  );
  const rawStoredImage = isEnhanced
    ? enhancedImages[props.field] || ''
    : design[props.platform][props.field];
  const storedImage =
    !isEnhanced &&
    props.platform === 'google' &&
    props.field === 'banner' &&
    rawStoredImage === GOOGLE_DEFAULT_WAVE_BANNER_BASE64
      ? ''
      : rawStoredImage;
  const sharedFallbackImage = !isOverrideUpload
    ? ''
    : getSharedFallbackImage(design, props.platform, props.field);
  const hasOwnImage = Boolean(storedImage);
  const isShowingSharedImage =
    isOverrideUpload && !storedImage && Boolean(sharedFallbackImage);
  const currentErrors = Object.keys(useAppSelector((s) => s.templateErrors));

  const errorKey = isEnhanced
    ? `${APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE}.${props.field}`
    : `images.${props.platform}.${props.field}`;
  const focusKey = isEnhanced
    ? `${APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE}.${props.field}`
    : `design.${props.platform}.${props.field}`;
  const indicatorPrefix = isEnhanced
    ? APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE
    : `design.${props.platform}`;
  const dimensionErrorPath = `${errorKey}.dimensions`;

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(storedImage || null);
  const displayPreview = demoPreview ?? preview ?? sharedFallbackImage ?? null;
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const { handleFocus } = useTemplateFieldValidation('images');

  const imageRef = useRef<HTMLDivElement>(null);
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    prefix: indicatorPrefix,
    fieldRefMap: { [field]: imageRef },
  });

  useEffect(() => {
    setPreview(storedImage || null);
  }, [storedImage, field]);

  useEffect(() => {
    if (!preview) {
      setImageDimensions(null);
      return;
    }

    let cancelled = false;
    const img = document.createElement('img');
    img.onload = () => {
      if (cancelled) return;
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = () => {
      if (!cancelled) setImageDimensions(null);
    };
    img.src = preview;

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [preview]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setSelectedImage(base64);
      onOpen();
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleValidation = (croppedBase64?: string | null) => {
    if (isEnhanced) {
      const updatedImages = {
        ...enhancedImages,
        [props.field]: croppedBase64 ?? '',
      };
      validateField(
        props.field,
        croppedBase64,
        {
          appleEnhancedDesign: {
            type: enhancedDesignType,
            images: updatedImages,
          },
          templateType,
        },
        true
      );
      return;
    }

    const platform = props.platform;
    const updatedDesign = {
      ...design,
      [platform]: {
        ...design[platform],
        [props.field]: croppedBase64,
      },
    };
    const relatedPaths = getRevalidationTargets(
      templateType,
      platform,
      props.field
    );
    const pathsToRevalidate = [
      ...relatedPaths.filter(
        (p) => croppedBase64 === null || currentErrors.includes(`images.${p}`)
      ),
      `${platform}.${props.field}`,
    ];

    pathsToRevalidate.forEach((p) => {
      const trimmed = p.replace(/^images\./, '');
      const plat = trimmed.split('.')[0] as Platform;
      const fld = trimmed.split('.')[1] as ImageField;
      const value = updatedDesign[plat][fld];
      validateField(
        p,
        value,
        {
          images: updatedDesign,
          templateType,
        },
        true
      );
    });
  };

  const handleCropComplete = (croppedBase64: string) => {
    setPreview(croppedBase64);
    if (isEnhanced) {
      dispatch(
        setAppleEnhancedDesignImage({
          field: props.field,
          value: croppedBase64,
        })
      );
    } else {
      dispatch(
        setDesignImage({
          platform: props.platform,
          field: props.field,
          value: croppedBase64,
        })
      );
    }
    handleValidation(croppedBase64);
    onClose();
  };

  const isSelected = focusedField === focusKey;

  const handleClick = () => {
    if (isDisabled) return;
    handleFocus(focusKey);
    if (isOverrideUpload && isShowingSharedImage && sharedFallbackImage) {
      setSelectedImage(sharedFallbackImage);
      onOpen();
      return;
    }
    if (preview && !demoPreview && !isStockDesignImage(preview)) {
      setSelectedImage(preview);
      onOpen();
      return;
    }
    inputRef.current?.click();
  };

  const handleRemove = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isDisabled) return;
    setPreview(null);
    if (isEnhanced) {
      dispatch(setAppleEnhancedDesignImage({ field: props.field, value: '' }));
    } else {
      dispatch(
        removeDesignImage({ platform: props.platform, field: props.field })
      );
    }
    const willSetPlaceholder =
      !isEnhanced &&
      props.field === 'banner' &&
      templateType !== 'transit' &&
      !design.shared.banner &&
      (props.platform === 'google' || !design.google.banner);
    handleValidation(
      willSetPlaceholder && props.platform === 'google'
        ? GOOGLE_DEFAULT_WAVE_BANNER_BASE64
        : null
    );
    if (willSetPlaceholder) {
      dispatch(clearError('images.google.banner'));
    }
    handleFocus(focusKey);
  };

  const dimensions = getImageDimensions(templateType, {
    expressivePassEnabled: props.expressivePassEnabled,
  });
  const maxDimensions = isEnhanced
    ? dimensions.enhancedEventTicket?.[props.field]
    : dimensions[props.platform]?.[props.field];
  const aspectRatio =
    maxDimensions?.width && maxDimensions?.height
      ? maxDimensions.width / maxDimensions.height
      : 1;

  const isImageTooLarge = useMemo(() => {
    if (!imageDimensions || !maxDimensions || isShowingSharedImage)
      return false;
    return (
      imageDimensions.width > maxDimensions.width ||
      imageDimensions.height > maxDimensions.height
    );
  }, [imageDimensions, maxDimensions, isShowingSharedImage]);

  useEffect(() => {
    // Clear error if image is null/removed
    if (!storedImage) {
      dispatch(clearError(dimensionErrorPath));
      return;
    }

    if (isImageTooLarge && maxDimensions) {
      dispatch(
        setError({
          path: dimensionErrorPath,
          message: `is too large. Maximum dimensions: ${maxDimensions.width} × ${maxDimensions.height}px`,
        })
      );
    } else {
      dispatch(clearError(dimensionErrorPath));
    }
  }, [
    storedImage,
    isImageTooLarge,
    maxDimensions,
    dimensionErrorPath,
    dispatch,
  ]);

  return (
    <FieldEditIndicator
      fieldRef={imageRef}
      isFieldBeingEdited={isFieldBeingEdited}
      activeFieldRef={activeFieldRef}
      left="-40px"
      top="80px"
    >
      <FormControl
        isRequired={isRequired}
        isInvalid={
          !!errors[errorKey] ||
          !!(isImageTooLarge && storedImage) ||
          !!errors[dimensionErrorPath]
        }
      >
        <Box opacity={isDisabled ? 0.6 : 1}>
          <FormLabel as="div" variant="templateBuilderLabel" mb={2}>
            {label}
          </FormLabel>
          <Box
            data-template-field
            position="relative"
            border="2px solid"
            borderColor={isSelected ? 'orangePrimary' : 'transparent'}
            borderRadius="10px"
            w="fit-content"
            h="auto"
            cursor={
              isDisabled
                ? 'not-allowed'
                : preview && !demoPreview && !isSelected
                  ? 'default'
                  : 'pointer'
            }
            transition="border-color 0.2s ease"
            onMouseEnter={() => {
              if (!isDisabled) handleFocus(focusKey);
            }}
            onClick={handleClick}
          >
            <Box
              boxSizing="border-box"
              w={size === 'compact' ? '112px' : '201px'}
              maxW={size === 'compact' ? '112px' : '201px'}
              h="77px"
              border="1px dashed"
              borderColor={isSelected ? 'transparent' : 'grey'}
              borderRadius="8px"
              bg={
                isShowingSharedImage
                  ? '#d9d9d9'
                  : displayPreview
                    ? undefined
                    : 'softWhite'
              }
              background={
                displayPreview && !isShowingSharedImage
                  ? 'repeating-conic-gradient(#C2C2C2 0% 25%, #D6D6D6 0% 50%) 0 0 / 30px 30px'
                  : undefined
              }
              p={3}
              transition="border-color 0.2s ease"
              _hover={
                !isDisabled && !isSelected
                  ? { borderColor: 'orangePrimary' }
                  : undefined
              }
            >
              <Flex
                direction="column"
                align="center"
                justify="center"
                textAlign="center"
                h="full"
                gap={1}
              >
                {displayPreview ? (
                  <>
                    <Image
                      src={displayPreview}
                      alt={`${label} preview`}
                      maxH="60px"
                      objectFit="contain"
                      borderRadius="5px"
                      draggable={false}
                      userSelect="none"
                      pointerEvents="none"
                      opacity={isShowingSharedImage ? 0.55 : 1}
                    />
                    {isShowingSharedImage && (
                      <Flex
                        position="absolute"
                        inset={0}
                        direction="column"
                        align="center"
                        justify="center"
                        gap={1}
                        color="white"
                        bg="rgba(0, 0, 0, 0.55)"
                        borderRadius="8px"
                        pointerEvents="none"
                      >
                        <Icon as={CustomUploadIcon} boxSize="24px" />
                        <Text fontSize="13px">
                          {`Replace ${(label || { wideLogo: 'logo', logo: 'icon', icon: 'icon' }[field] || field).toLowerCase()} for ${props.platform}`}
                        </Text>
                      </Flex>
                    )}
                    {!demoPreview && imageDimensions && isImageTooLarge && (
                      <Text
                        fontSize="11px"
                        color={isImageTooLarge ? 'red.500' : 'grey'}
                        mt={1}
                      >
                        {imageDimensions.width} × {imageDimensions.height}px
                      </Text>
                    )}
                    {!isDisabled && hasOwnImage && (
                      <Box position="absolute" top="3px" right="4px">
                        <Box
                          as="button"
                          type="button"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          w="22px"
                          h="22px"
                          p={0}
                          lineHeight={0}
                          color="#D01111"
                          sx={{
                            svg: {
                              transition: 'transform 0.2s ease',
                            },
                          }}
                          _hover={{
                            svg: { transform: 'scale(1.15)' },
                          }}
                          _active={{
                            svg: { transform: 'scale(1.05)' },
                          }}
                          onClick={handleRemove}
                        >
                          <Icon as={CustomCloseIcon} boxSize="14px" />
                        </Box>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box>
                    <Icon
                      as={CustomUploadIcon}
                      boxSize="22px"
                      color="orangePrimary"
                      mb={2}
                    />
                    {maxDimensions && (
                      <Text fontSize="8px">
                        {props.expressivePassEnabled &&
                        templateType !== 'transit'
                          ? 'Wide or square image'
                          : `Ideally: ${maxDimensions.width} × ${maxDimensions.height}px`}
                      </Text>
                    )}
                    <Text fontSize="8px" color="#4C4C4C" mt={'2px'}>
                      File type: png/jpg/jpeg
                    </Text>
                  </Box>
                )}
              </Flex>
            </Box>
          </Box>

          <Input
            ref={inputRef}
            type="file"
            accept=".png, .jpg, .jpeg"
            onChange={handleFileChange}
            display="none"
            name={errorKey}
          />
        </Box>

        {((isImageTooLarge && maxDimensions && storedImage) ||
          errors[dimensionErrorPath]) && (
          <FormErrorMessage
            pl={2}
            display="flex"
            alignItems="center"
            gap={1.5}
            mt={2}
          >
            <CustomErrorIcon />
            {errors[dimensionErrorPath]
              ? `Image ${errors[dimensionErrorPath]}`
              : `Image is too large. Maximum dimensions: ${maxDimensions?.width} × ${maxDimensions?.height}px`}
          </FormErrorMessage>
        )}

        {isOpen && selectedImage && (
          <CropModal
            isOpen={isOpen}
            onClose={onClose}
            imageSrc={selectedImage}
            aspect={aspectRatio}
            platform={isEnhanced ? 'enhancedEventTicket' : props.platform}
            field={field}
            onCropComplete={handleCropComplete}
            discardUnchanged={isShowingSharedImage}
            expressivePassEnabled={props.expressivePassEnabled}
          />
        )}

        {errors[errorKey] && (
          <FormErrorMessage pl={2} display="flex" alignItems="center" gap={1.5}>
            <CustomErrorIcon />
            {errors[errorKey]}
          </FormErrorMessage>
        )}
      </FormControl>
    </FieldEditIndicator>
  );
};

export default ImageUploadField;
